import { hostBrand, brandPath } from "@/lib/brand";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useOperator } from "@/context/OperatorContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { apiError } from "@/lib/api";
import { MorrMark } from "@/components/MorrMark";

function routeFor(role) {
  if (role === "superadmin") return brandPath("/admin");
  if (role === "tenant") return brandPath("/dashboard");
  return null;
}

export default function OperatorLogin() {
  const { operator, login } = useOperator();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [usePw, setUsePw] = useState(false);

  useEffect(() => {
    if (operator && operator.access) {
      const r = routeFor(operator.role);
      if (r) navigate(r);
    }
  }, [operator, navigate]);

  const doLogin = async (em, pw) => {
    setError(""); setLoading(true);
    try {
      const res = await login(em, pw);
      if (res.redirected) return;
      const r = routeFor(res.role);
      if (r) navigate(r);
      else setError(`Your MorrAccess account isn't assigned to a ${hostBrand().name} role yet. Ask the platform owner to assign you a store.`);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submit = (e) => { e.preventDefault(); doLogin(email, password); };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-[0.06]" />
        <Link to="/" className="flex items-center gap-2 relative">
          <MorrMark size={28} />
          <span className="font-display font-extrabold tracking-tighter text-xl" data-testid="login-brand">{hostBrand().name}</span>
        </Link>
        <div className="relative">
          <h1 className="font-display font-extrabold tracking-tighter text-4xl leading-[0.95]">Operator console</h1>
          <p className="mt-4 text-white/60 max-w-sm leading-relaxed">Platform owners and business owners sign in with their MorrAccess account. Access is gated by your {hostBrand().name} entitlement.</p>
        </div>
        <div className="relative flex items-center gap-2 text-sm text-white/50">
          <ShieldCheck className="h-4 w-4" /> Secured by MorrAccess SSO
        </div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <MorrMark size={28} />
            <span className="font-display font-extrabold tracking-tighter text-xl" data-testid="login-brand">{hostBrand().name}</span>
          </div>
          <h2 className="font-display font-extrabold tracking-tighter text-2xl">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Business owners &amp; staff sign in with MorrAccess.</p>

          <Button type="button" onClick={() => navigate(brandPath("/sso/redirect"))} className="mt-8 w-full rounded-none h-11 gap-2" data-testid="operator-sso-btn">
            <ShieldCheck className="h-4 w-4" /> Continue with MorrAccess
          </Button>
          <p className="mt-2 text-[11px] text-muted-foreground text-center">You'll sign in securely at access.morrstore.com and return here.</p>

          {!usePw ? (
            <button type="button" onClick={() => setUsePw(true)} className="mt-5 text-xs text-muted-foreground hover:text-foreground underline w-full text-center" data-testid="operator-usepw-toggle">
              Sign in with a password instead
            </button>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4 border-t border-border pt-6" data-testid="operator-login-form">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Password sign-in (fallback)</p>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com" className="rounded-none mt-1.5 h-11" data-testid="operator-email-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="rounded-none mt-1.5 h-11" data-testid="operator-password-input" />
              </div>
              {error && <p className="text-sm text-accent" data-testid="operator-login-error">{error}</p>}
              <Button type="submit" disabled={loading} variant="outline" className="w-full rounded-none h-11" data-testid="operator-login-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in with password"}
              </Button>
            </form>
          )}

          <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
            No {hostBrand().name} access? You'll be redirected to the Morr Store to purchase, then returned here automatically.
          </p>

          <Link to="/" className="mt-6 inline-block text-sm text-primary hover:underline">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
